/**
 * FallingState - Player is descending (after jump apex or walking off platform)
 */

import BaseState from './BaseState';
import Phaser from 'phaser';
import eventBus, { GameEvents } from '../../core/EventBus';

export default class FallingState extends BaseState {
    constructor() {
        super();
        this.maxSpeed = 13;
        this.acceleration = 0.012;
        this.angularSpeed = 0.18;
        this.doubleJumpForce = -12;
        this.hasDoubleJumped = false;
    }

    enter(data) {
        this.hasDoubleJumped = data?.hasDoubleJumped || false;
        
        // If entering falling without having jumped (walked off), allow coyote jump
        if (this.player.jumpCount === 0) {
            this.player.lastGroundedTime = this.scene.time.now;
        }
    }

    update(delta) {
        if (!this.player || this.player.isDead) return;

        const { cursors, wasd } = this.player;
        const velocity = this.sprite.body.velocity;

        // Air control
        const moveLeft = cursors.left.isDown || wasd.left.isDown;
        const moveRight = cursors.right.isDown || wasd.right.isDown;

        // Dash (if active, skip normal movement but keep magnetic pull)
        if (this.player.handleDash(moveLeft, moveRight)) {
            this.player.handleMagneticPull();
            this.applyVisualStretch(velocity);
            return;
        }

        if (moveLeft && velocity.x > -this.maxSpeed) {
            this.sprite.setAngularVelocity(-this.angularSpeed);
            this.sprite.applyForce({ x: -this.acceleration, y: 0 });
        } else if (moveRight && velocity.x < this.maxSpeed) {
            this.sprite.setAngularVelocity(this.angularSpeed);
            this.sprite.applyForce({ x: this.acceleration, y: 0 });
        }

        // Coyote time - allow jump if just fell off platform
        const canCoyoteJump = (this.scene.time.now - this.player.lastGroundedTime) < this.player.coyoteTime && this.player.jumpCount === 0;
        
        if (canCoyoteJump && this.player.checkJumpInputJustPressed()) {
            this.transitionTo('jumping');
            return;
        }

        // Double jump while falling (if not used)
        if (!this.hasDoubleJumped && this.player.jumpCount <= 1) {
            if (this.player.checkJumpInputJustPressed()) {
                this.hasDoubleJumped = true;
                this.sprite.setVelocityY(this.doubleJumpForce);
                this.player.jumpCount = 2;
                this.player.applySquash(0.7, 1.3);
                this.player.createDoubleJumpParticles();
                eventBus.emit(GameEvents.PLAYER_DOUBLE_JUMP);
                this.transitionTo('jumping', { isDoubleJump: true });
                return;
            }
        }

        // Check for landing
        if (this.player.isGrounded) {
            this.player.jumpCount = 0;
            eventBus.emit(GameEvents.PLAYER_LAND);
            
            // Slight squash on land
            this.player.applySquash(1.2, 0.8);
            
            // Create landing dust particles based on fall velocity
            // Only if falling fast enough (velocity.y > 5)
            if (velocity.y > 5) {
                this.player.createLandingDust(velocity.y);
            }
            
            this.transitionTo(moveLeft || moveRight ? 'rolling' : 'idle');
            return;
        }

        // Handle magnetic pull while falling
        this.player.handleMagneticPull();

        // Visual stretch (elongate when falling fast)
        this.applyVisualStretch(velocity);
    }

    applyVisualStretch(velocity) {
        const stretchY = 1 + (velocity.y * 0.01);
        const stretchX = 1 - (velocity.y * 0.005);
        
        this.sprite.setScale(
            Phaser.Math.Clamp(stretchX * this.player.baseScaleX, 0.7, 1.3),
            Phaser.Math.Clamp(stretchY * this.player.baseScaleY, 0.7, 1.3)
        );
        
        if (this.player.highlight) {
            this.player.highlight.setScale(this.sprite.scaleX, this.sprite.scaleY);
        }
    }

    exit() {
        // Nothing specific
    }
}
